import { APIGet, APIPost, APIPut, APIDelete, APIUrl, AdminType } from "api/protocols";
import { CancelToken } from "axios";
import { ITicket } from "redux/reducers/models/ticket";

const apiVersion = "2024-04";
const adminType = "facility-admin";
const baseUrl = "/" + apiVersion + "/" + adminType + "/ticket";

export type TGetTicket = {
  id?: number;
  customer_id?: number;
  /** Search by title and subtitle */
  search?: string;
  extended?: boolean;
  limit?: number;
  offset?: number;
  archived?: 0 | 1;
};

export type TPostTicketBase = {
  id: number;
  title: string;
  subtitle?: string;
  included?: string;
  terms?: string[];
  description?: string;
  logo_image?: string;
  value?: number;
  valid_facilities?: number[];
  product_redemption: any;
  multi_use?: boolean;
  valid_start_date?: string;
  valid_end_date?: string;
};

export type TPostTicketDate = TPostTicketBase & {
  expiry_type?: "date";
  expiry_date: Date;
};

export type TPostTicketDays = TPostTicketBase & {
  expiry_type?: "days";
  expiry_days: number;
};

export type TPostTicketNone = TPostTicketBase & {
  expiry_type?: "none";
};

export type TPostTicket = TPostTicketDate | TPostTicketDays | TPostTicketNone;

type TPostRedemptionProducts = {
  ticket_id: number;
  //Single variant
  variant_id?: number;
  quantity?: number;
  //Multi-variant
  variants?: Array<{
    variant_id: number;
    quantity: number;
  }>;
};

export type TPutTicket = {
  id: number;
  title?: string;
  subtitle?: string;
  description?: string;
  expiry_type?: string;
  expiry_date?: string | Date;
  expiry_days?: number;
  valid_facilities?: number[];
  valid_start_date?: string;
  valid_end_date?: string;
  share_limit?: string | number;
  multi_use?: boolean;
  transfers_disabled?: boolean;
};

// GET Tickets
export async function GetTickets(params: TGetTicket, useGlobalLoader: boolean, token?: CancelToken) {
  return await APIGet<ITicket[]>(APIUrl(AdminType.FacilityAdmin, "/ticket"), params, useGlobalLoader, token);
}

// POST Ticket
export async function PostTicket(params: TPostTicket, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/ticket"), params, useGlobalLoader);
}

// PUT Ticket
export async function PutTicket(params: TPutTicket, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/ticket"), params, useGlobalLoader);
}

//DELETE Ticket
export async function ArchiveTicket(params: TPutTicket, useGlobalLoader: boolean) {
  return await APIPut(baseUrl + "/archive", params, useGlobalLoader);
}

export async function GetSharedTickets(
  params: { customer_id: number },
  useGlobalLoader: boolean,
  cancelToken?: CancelToken,
) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/ticket/share"), params, useGlobalLoader, cancelToken);
}

//POST Attach ticket to a product or variant
export async function AttachTicket(
  params: { ticket_id: number; variant_id: number; quantity: number },
  useGlobalLoader: boolean,
) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/ticket/attach"), params, useGlobalLoader);
}

//DELETE Detach ticket from a product or variant
export async function DetachTicket(params: { ticket_id: number; variant_id: number }, useGlobalLoader: boolean) {
  return await APIDelete(APIUrl(AdminType.FacilityAdmin, "/ticket/detach"), params, useGlobalLoader);
}

export async function PostTicketImage(params: FormData, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/ticket/image"), params, useGlobalLoader);
}

export async function DeleteTicketImage(params: { id: number }, useGlobalLoader: boolean) {
  return await APIDelete(APIUrl(AdminType.FacilityAdmin, "/ticket/image"), params, useGlobalLoader);
}

export async function RemoveRedemptionProduct(params: any, useGlobalLoader: boolean) {
  return await APIDelete(APIUrl(AdminType.FacilityAdmin, "/ticket/redemption/detach"), params, useGlobalLoader);
}

interface IGetTicketStub {
  code?: string;
  customer_id?: number;
  id?: number;
  status?: string;
  search?: string;
  extended?: boolean;
  valid_facilities?: boolean;
  shared?: boolean; // Returns tickets shared with the user
  redemption_products?: boolean; // Returns the ticket's redemption products
  limit?: number;
  offset?: number;
}

interface IPutTicketStub {
  code?: string;
  customer_id?: number;
  id?: number;
  status?: string;
  search?: string;
  extended?: boolean;
}
//GET Ticket Stub
export async function GetTicketStub(params: IGetTicketStub, useGlobalLoader: boolean, token?: CancelToken) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/ticket/stub"), params, useGlobalLoader, token);
}

//PUT Ticket Stub
export async function PutTicketStub(params: IPutTicketStub, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/ticket/stub"), params, useGlobalLoader);
}

interface IGetTicketStubPdf {
  order_id?: number;
  order_line_item_id?: number;
  ticket_stub_ids?: any[];
  format?: "zebra";
}

export async function GetTicketStubPdf(params: IGetTicketStubPdf, useGlobalLoader: boolean) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/ticket/stub/pdf"), params, useGlobalLoader);
}

interface IGetTicketStubReceipt {
  order_id?: number;
  order_line_item_id?: number;
  ticket_stub_ids?: any[];
}

export async function GetTicketStubReceipt(params: IGetTicketStubReceipt, useGlobalLoader: boolean) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/ticket/stub/receipt"), params, useGlobalLoader);
}

// Redeem Ticket Stub
export async function PutRedeem(params: { code: string }, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, `/stub/redeem`), params, useGlobalLoader);
}

export async function AddRedemptionProducts(params: TPostRedemptionProducts, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/ticket/redemption/attach"), params, useGlobalLoader);
}

interface ITransferTicketStub {
  selected_ticket_stub_ids: Array<number>;
  first_name: string;
  last_name: string;
  email_address: string;
  customer_sender_id: number;
}
export async function PostTransferTicketStub(params: ITransferTicketStub, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/ticket/stub/transfer"), params, useGlobalLoader);
}

interface IVoidTicketStub {
  selected_ticket_stub_ids: number[];
}
// Void Ticket Stub
export async function PutVoidTicketStub(params: IVoidTicketStub, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/ticket/stub/void"), params, useGlobalLoader);
}

interface IPutRedeemMultiple {
  ticket_stub_ids: number[];
}
export async function PutRedeemMultiple(params: IPutRedeemMultiple, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/ticket/stub/redeem/multiple"), params, useGlobalLoader);
}
